import { useCallback, useEffect, useRef, useState } from "react";

const DEBOUNCE_MS = 500;
// Quiet automatic retries for a flaky connection, before we ask the person to retry.
const AUTO_RETRY_DELAYS_MS = [1200, 2800];

/**
 * status:
 *  - idle        nothing typed yet
 *  - current     the number is the one this member already has (edit screens). No lookup needed.
 *  - checking    a lookup is running (or about to)
 *  - available   the number is free
 *  - taken       the number belongs to another member (a real answer)
 *  - unverified  the lookup could not run (network or server problem). Not an answer.
 */
export const MEMBER_NO_STATUS = {
  IDLE: "idle",
  CURRENT: "current",
  CHECKING: "checking",
  AVAILABLE: "available",
  TAKEN: "taken",
  UNVERIFIED: "unverified",
};

/** True when a failure is the connection's fault and a retry may well work. */
export function isTransientError(error) {
  const status = error?.status;
  if (status === undefined || status === null) return true; // thrown without a response
  if (status === "FETCH_ERROR" || status === "TIMEOUT_ERROR") return true;
  if (typeof status === "number") return status >= 500 || status === 408 || status === 429;
  return false;
}

export function describeFailure(error) {
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return "You appear to be offline. Reconnect, then try again.";
  }
  if (isTransientError(error)) {
    return "We could not reach the server. Check your connection, then try again.";
  }
  return error?.data?.message || "We could not check this number. Please try again.";
}

/**
 * Live check that a membership number is free.
 * `check(memberNo)` must resolve to `{ available: boolean }` or reject with the failure.
 * Pass `currentMemberNo` on an edit screen: typing the member's own number is never "taken".
 */
const sameNumber = (a, b) =>
  String(a ?? "").trim().toLowerCase() === String(b ?? "").trim().toLowerCase();

export default function useMemberNoAvailability(
  memberNo,
  { check, enabled = true, currentMemberNo = "" }
) {
  const [state, setState] = useState({ status: MEMBER_NO_STATUS.IDLE, message: "" });
  const runRef = useRef(0); // only the newest lookup may update the screen
  const checkRef = useRef(check);
  checkRef.current = check;
  const timersRef = useRef([]);

  const clearTimers = () => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  };

  const run = useCallback(async (value, runId, attempt = 0) => {
    try {
      const result = await checkRef.current(value);
      if (runId !== runRef.current) return;
      const available = Boolean(result?.available);
      setState({
        status: available ? MEMBER_NO_STATUS.AVAILABLE : MEMBER_NO_STATUS.TAKEN,
        message: available ? "Available" : "Already taken by another member in this cooperative",
      });
    } catch (error) {
      if (runId !== runRef.current) return;
      const offline = typeof navigator !== "undefined" && navigator.onLine === false;
      if (!offline && isTransientError(error) && attempt < AUTO_RETRY_DELAYS_MS.length) {
        const timer = setTimeout(() => run(value, runId, attempt + 1), AUTO_RETRY_DELAYS_MS[attempt]);
        timersRef.current.push(timer);
        return; // still showing "checking"
      }
      setState({ status: MEMBER_NO_STATUS.UNVERIFIED, message: describeFailure(error) });
    }
  }, []);

  // Look up the number shortly after the person stops typing.
  useEffect(() => {
    clearTimers();
    const trimmed = String(memberNo ?? "").trim();
    const runId = ++runRef.current;
    if (!enabled || !trimmed) {
      setState({ status: MEMBER_NO_STATUS.IDLE, message: "" });
      return undefined;
    }
    if (currentMemberNo && sameNumber(trimmed, currentMemberNo)) {
      setState({ status: MEMBER_NO_STATUS.CURRENT, message: "This is the member's current number" });
      return undefined;
    }
    setState({ status: MEMBER_NO_STATUS.CHECKING, message: "" });
    const timer = setTimeout(() => run(trimmed, runId), DEBOUNCE_MS);
    timersRef.current.push(timer);
    return () => {
      clearTimers();
    };
  }, [memberNo, enabled, run, currentMemberNo]);

  /** Check again right now (the Try again button). */
  const retry = useCallback(() => {
    const trimmed = String(memberNo ?? "").trim();
    if (!enabled || !trimmed || (currentMemberNo && sameNumber(trimmed, currentMemberNo))) return;
    clearTimers();
    const runId = ++runRef.current;
    setState({ status: MEMBER_NO_STATUS.CHECKING, message: "" });
    run(trimmed, runId);
  }, [memberNo, enabled, run, currentMemberNo]);

  // When the connection comes back, try again without being asked.
  useEffect(() => {
    if (state.status !== MEMBER_NO_STATUS.UNVERIFIED) return undefined;
    window.addEventListener("online", retry);
    return () => window.removeEventListener("online", retry);
  }, [state.status, retry]);

  return {
    status: state.status,
    message: state.message,
    isChecking: state.status === MEMBER_NO_STATUS.CHECKING,
    isAvailable:
      state.status === MEMBER_NO_STATUS.AVAILABLE || state.status === MEMBER_NO_STATUS.CURRENT
        ? true
        : state.status === MEMBER_NO_STATUS.IDLE
          ? null
          : false,
    retry,
  };
}
